/**
 * The one way this app talks to the backend.
 *
 * Adds the bearer token, normalizes both error shapes, and notifies a single
 * listener when the session is genuinely gone. Nothing else in the codebase calls
 * fetch directly — the previous version had a raw fetch in its axios module that
 * skipped auth entirely.
 */

import { API_BASE_URL } from '@/lib/env';
import { ApiError, toApiError } from './errors';
import { readToken } from '@/auth/token';

type UnauthorizedListener = () => void;

let onUnauthorized: UnauthorizedListener | null = null;

/** Registered once by the auth provider. */
export function setUnauthorizedHandler(handler: UnauthorizedListener | null): void {
  onUnauthorized = handler;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  /** Serialized as JSON. Use `form` for the OAuth2 login endpoint instead. */
  body?: unknown;
  /** Sent as application/x-www-form-urlencoded. */
  form?: Record<string, string>;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
  /** Skip the bearer header — only login and register. */
  anonymous?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${API_BASE_URL}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function send(path: string, options: RequestOptions = {}): Promise<Response> {
  const { method = 'GET', body, form, query, signal, anonymous } = options;

  const headers: Record<string, string> = {};
  if (!anonymous) {
    const token = readToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let payload: BodyInit | undefined;
  if (form) {
    headers['Content-Type'] = 'application/x-www-form-urlencoded';
    payload = new URLSearchParams(form).toString();
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  const response = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: payload,
    signal,
  });

  if (response.ok) return response;

  const error = await toApiError(response);

  // Only a 401 means the session died. Ownership failures answer 404 by design,
  // and signing the user out on those would be a bug.
  if (error.isAuthFailure && !anonymous) {
    onUnauthorized?.();
  }

  throw error;
}

export async function apiGet<T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
  const response = await send(path, { ...options, method: 'GET' });
  return (await response.json()) as T;
}

export async function apiPost<T>(path: string, options?: Omit<RequestOptions, 'method'>): Promise<T> {
  const response = await send(path, { ...options, method: 'POST' });
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export async function apiPatch<T>(path: string, options?: Omit<RequestOptions, 'method'>): Promise<T> {
  const response = await send(path, { ...options, method: 'PATCH' });
  return (await response.json()) as T;
}

export async function apiDelete<T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
  const response = await send(path, { ...options, method: 'DELETE' });
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** For endpoints that stream a file rather than JSON (the telemetry CSV export). */
export async function apiGetBlob(
  path: string,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<Blob> {
  const response = await send(path, { ...options, method: 'GET' });
  return await response.blob();
}

export { ApiError };
